import React from "react";
import { Form, Input, Select, InputNumber, Switch, Card, Divider, Row, Col, Space, Tag, Button, message } from "antd";
import type { CanvasTemplate, CanvasSettings } from "../types/aiTools";

const { TextArea } = Input;

interface CanvasTemplateEditorProps {
  value?: CanvasTemplate;
  onChange?: (value: CanvasTemplate) => void;
}

const defaultCanvasSettings: CanvasSettings = {};

export const defaultCanvasTemplate: CanvasTemplate = {
  type: "json",
  baseTemplate: undefined,
  jsonSchema: undefined,
  htmlFileBase64: undefined,
  htmlFileName: undefined,
  cssFileBase64: undefined,
  cssFileName: undefined,
  editableFields: [],
  canvasSettings: defaultCanvasSettings,
  isDynamic: false,
};

export const CanvasTemplateEditor: React.FC<CanvasTemplateEditorProps> = ({
  value = defaultCanvasTemplate,
  onChange,
}) => {
  const updateTemplate = (updates: Partial<CanvasTemplate>) => {
    onChange?.({ ...value, ...updates });
  };

  const updateSettings = (updates: Partial<CanvasSettings>) => {
    onChange?.({
      ...value,
      canvasSettings: { ...value.canvasSettings, ...updates },
    });
  };

  const readFileAsBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        if (typeof reader.result !== "string") {
          reject(new Error("Invalid file content"));
          return;
        }
        const base64 = reader.result.split(",")[1] || "";
        resolve(base64);
      };

      reader.onerror = () => {
        reject(new Error("File read failed"));
      };

      reader.readAsDataURL(file);
    });

  const handleTemplateFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    fileType: "html" | "css",
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const expectedExtension = fileType === "html" ? ".html" : ".css";
    if (!file.name.toLowerCase().endsWith(expectedExtension)) {
      message.error(`Wybierz plik ${expectedExtension}`);
      event.target.value = "";
      return;
    }

    try {
      const base64 = await readFileAsBase64(file);
      if (fileType === "html") {
        updateTemplate({
          htmlFileBase64: base64,
          htmlFileName: file.name,
        });
      } else {
        updateTemplate({
          cssFileBase64: base64,
          cssFileName: file.name,
        });
      }
    } catch (_error) {
      message.error("Nie udało się wczytać pliku");
    } finally {
      // Allow selecting the same file again after validation/read.
      event.target.value = "";
    }
  };

  const clearTemplateFile = (fileType: "html" | "css") => {
    if (fileType === "html") {
      updateTemplate({
        htmlFileBase64: undefined,
        htmlFileName: undefined,
      });
      return;
    }

    updateTemplate({
      cssFileBase64: undefined,
      cssFileName: undefined,
    });
  };

  return (
    <Card size="small">
      <Row gutter={16}>
        <Col span={6}>
          <Form.Item label="Typ szablonu" style={{ marginBottom: 8 }}>
            <Select
              value={value.type}
              onChange={(v) => updateTemplate({ type: v })}
              options={[
                { value: "json", label: "Strukturalny" },
                { value: "freeform", label: "Swobodny" },
                { value: "markdown", label: "Markdown" },
                { value: "html", label: "HTML" },
              ]}
              defaultValue={"json"}
            />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item label="Dynamiczny" style={{ marginBottom: 8 }}>
            <Switch
              checked={value.isDynamic}
              onChange={(v) => updateTemplate({ isDynamic: v })}
              defaultValue={false}
            />
          </Form.Item>
        </Col>
        <Col span={14}>
          <Form.Item label="Edytowalne pola" style={{ marginBottom: 8 }}>
            <Select
              mode="tags"
              value={value.editableFields}
              onChange={(v) => updateTemplate({ editableFields: v })}
              placeholder="Dodaj nazwy pól..."
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Col>
      </Row>
      {value.type === "json" ? (
        <Form.Item label="Schemat JSON" style={{ marginBottom: 8 }}>
          <TextArea
            rows={6}
            value={value.jsonSchema}
            onChange={(e) => updateTemplate({ jsonSchema: e.target.value })}
            placeholder="Wprowadź schemat JSON"
            style={{ fontFamily: "monospace" }}
          />
        </Form.Item>
      ) : null}
      {value.type === "markdown" || value.type === "html" ? (
        <Form.Item label="Bazowy szablon" style={{ marginBottom: 8 }}>
          <TextArea
            rows={6}
            value={value.baseTemplate}
            onChange={(e) => updateTemplate({ baseTemplate: e.target.value })}
            placeholder="Wprowadź szablon z placeholderami, np. {{content}}, {{title}}..."
            style={{ fontFamily: "monospace" }}
          />
        </Form.Item>
      ) : null}

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item 
            label="Plik HTML (.html)" 
            style={{ marginBottom: 8 }}
            rules={[{ required: true, message: "Template HTML jest wymagany" }]}
          >
            <Space orientation="vertical" style={{ width: "100%" }} size={8}>
              <input
                type="file"
                accept=".html,text/html"
                onChange={(event) => void handleTemplateFileChange(event, "html")}
              />
              <Space>
                <Tag color={value.htmlFileBase64 ? "blue" : "default"}>
                  {value.htmlFileName || "Brak pliku"}
                </Tag>
                <Button
                  size="small"
                  disabled={!value.htmlFileBase64}
                  onClick={() => clearTemplateFile("html")}
                >
                  Wyczyść
                </Button>
              </Space>
            </Space>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Plik CSS (.css)" style={{ marginBottom: 8 }}>
            <Space orientation="vertical" style={{ width: "100%" }} size={8}>
              <input
                type="file"
                accept=".css,text/css"
                onChange={(event) => void handleTemplateFileChange(event, "css")}
              />
              <Space>
                <Tag color={value.cssFileBase64 ? "blue" : "default"}>
                  {value.cssFileName || "Brak pliku"}
                </Tag>
                <Button
                  size="small"
                  disabled={!value.cssFileBase64}
                  onClick={() => clearTemplateFile("css")}
                >
                  Wyczyść
                </Button>
              </Space>
            </Space>
          </Form.Item>
        </Col>
      </Row>


      <Divider orientation="horizontal" style={{ margin: "12px 0" }}>
        Canvas Settings
      </Divider>

      <Row gutter={16}>
        <Col span={6}>
          <Form.Item label="Brand color" style={{ marginBottom: 8 }}>
            <Space>
              <Input
                type="color"
                value={value.canvasSettings?.brandColor || "#1F8081"}
                onChange={(e) => updateSettings({ brandColor: e.target.value })}
                style={{ width: 50, height: 32 }}
              />
              <Input
                value={value.canvasSettings?.brandColor || ""}
                onChange={(e) => updateSettings({ brandColor: e.target.value })}
                placeholder="#1F8081"
                style={{ width: 100 }}
              />
            </Space>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Accent color" style={{ marginBottom: 8 }}>
            <Space>
              <Input
                type="color"
                value={value.canvasSettings?.accentColor || "#FF6B6B"}
                onChange={(e) => updateSettings({ accentColor: e.target.value })}
                style={{ width: 50, height: 32 }}
              />
              <Input
                value={value.canvasSettings?.accentColor || ""}
                onChange={(e) => updateSettings({ accentColor: e.target.value })}
                placeholder="#FF6B6B"
                style={{ width: 100 }}
              />
            </Space>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Font family" style={{ marginBottom: 8 }}>
            <Select
              value={value.canvasSettings?.fontFamily}
              onChange={(v) => updateSettings({ fontFamily: v })}
              allowClear
              placeholder="Roboto"
              options={[
                { value: "Roboto", label: "Roboto" },
                { value: "Open Sans", label: "Open Sans" },
                { value: "Lato", label: "Lato" },
                { value: "Montserrat", label: "Montserrat" },
                { value: "Source Sans Pro", label: "Source Sans Pro" },
                { value: "Inter", label: "Inter" },
              ]}
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Base font size (px)" style={{ marginBottom: 8 }}>
            <InputNumber
              value={value.canvasSettings?.baseFontSize}
              onChange={(v) => updateSettings({ baseFontSize: v || undefined })}
              min={8}
              max={32}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={6}>
          <Form.Item label="Content padding (px)" style={{ marginBottom: 8 }}>
            <InputNumber
              value={value.canvasSettings?.contentPadding}
              onChange={(v) => updateSettings({ contentPadding: v || undefined })}
              min={0}
              max={100}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Border radius (px)" style={{ marginBottom: 8 }}>
            <InputNumber
              value={value.canvasSettings?.borderRadius}
              onChange={(v) => updateSettings({ borderRadius: v || undefined })}
              min={0}
              max={32}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item label="Show logo" style={{ marginBottom: 8 }}>
            <Switch
              checked={value.canvasSettings?.showLogo ?? true}
              onChange={(v) => updateSettings({ showLogo: v })}
            />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item label="Show header" style={{ marginBottom: 8 }}>
            <Switch
              checked={value.canvasSettings?.showHeader ?? true}
              onChange={(v) => updateSettings({ showHeader: v })}
            />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item label="Header style" style={{ marginBottom: 8 }}>
            <Select
              value={value.canvasSettings?.headerStyle}
              onChange={(v) => updateSettings({ headerStyle: v })}
              allowClear
              placeholder="line"
              options={[
                { value: "line", label: "Linia" },
                { value: "shadow", label: "Cień" },
                { value: "none", label: "Brak" },
              ]}
            />
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="horizontal" style={{ margin: "12px 0" }}>
        Podgląd ustawień
      </Divider>

      <div
        style={{
          padding: value.canvasSettings?.contentPadding || 16,
          borderRadius: value.canvasSettings?.borderRadius || 8,
          border: "1px solid #d9d9d9",
          backgroundColor: "#fafafa",
        }}
      >
        {value.canvasSettings?.showHeader && (
          <div
            style={{
              borderBottom:
                value.canvasSettings?.headerStyle === "line"
                  ? "1px solid #d9d9d9"
                  : value.canvasSettings?.headerStyle === "shadow"
                    ? "1px solid #e8e8e8"
                    : "none",
              boxShadow:
                value.canvasSettings?.headerStyle === "shadow"
                  ? "0 2px 8px rgba(0,0,0,0.06)"
                  : "none",
              paddingBottom: 8,
              marginBottom: 16,
            }}
          >
            <Space>
              {value.canvasSettings?.showLogo && (
                <div
                  style={{
                    width: 32,
                    height: 32,
                    backgroundColor: value.canvasSettings?.brandColor || "#1F8081",
                    borderRadius: 4,
                  }}
                />
              )}
              <span
                style={{
                  fontSize: value.canvasSettings?.baseFontSize ? value.canvasSettings.baseFontSize * 1.5 : 18,
                  fontWeight: 600,
                  fontFamily: value.canvasSettings?.fontFamily,
                  color: value.canvasSettings?.brandColor || "#1F8081",
                }}
              >
                Przykładowy tytuł
              </span>
            </Space>
          </div>
        )}
        <p
          style={{
            fontFamily: value.canvasSettings?.fontFamily,
            fontSize: value.canvasSettings?.baseFontSize || 14,
            color: "#333",
            margin: 0,
          }}
        >
          Przykładowa treść dokumentu. Tutaj pojawi się wygenerowany wynik.
        </p>
        <div style={{ marginTop: 16 }}>
          <Tag color={value.canvasSettings?.brandColor || "#1F8081"}>Tag 1</Tag>
          <Tag color={value.canvasSettings?.accentColor || "#FF6B6B"}>Tag 2</Tag>
        </div>
      </div>
    </Card>
  );
};

export default CanvasTemplateEditor;
